import React, { useRef } from "react"
import { SortExpression } from "../../../Models/Client/SortExpression"
import { ExclusiveChoiceDropdown } from "dash-styled"
import { ButtonWithPanel } from "./ButtonWithPanel"
import { useBoolean } from "../../../Hooks/useBoolean"
import { useOutsideClick } from "../../../Hooks/useOutsideClick"

/**
 * Configuration for a sorting option
 */
export interface SortOptionField<M> {
    /**
     * Field in the model `M` to sort by
     */
    field: keyof M
    /**
     * Text to be shown in the dropdown to choose sort field (i.e. human readable name of `field`)
     */
    label: string
    /**
     * Text to be shown in the dropdown to choose sort direction.
     * If omitted, *Ascending* is used.
     */
    ascLabel?: string
    /**
     * Text to be shown in the dropdown to choose sort direction.
     * If omitted, *Descending* is used.
     */
    descLabel?: string
}

/**
 * Configuration for sorting functionality
 */
export interface SortChooserProps<M> {
    /**
     * List of available fields for sorting
     */
    fields: SortOptionField<M>[]
    /**
     * Sorting which is currently applied
     */
    applied: SortExpression<M>
    /**
     * Callback function to handle changing sorting
     */
    onChange: (value: SortExpression<M>) => void
    classNameClosed?: string
    classNameOpen?: string
    testId?: string
    disabled?: boolean
}

export function SortChooser<M>({
    applied,
    fields,
    onChange,
    classNameClosed,
    classNameOpen,
    testId,
    disabled
}: SortChooserProps<M>) {
    const [isOpen, , close, toggleOpen] = useBoolean(false)
    const ref = useRef<HTMLDivElement>(null)
    const selectedFieldIndex = fields.findIndex(f => f.field === applied.sortBy)
    const selectedField = fields[selectedFieldIndex]

    useOutsideClick(ref, close)

    if (selectedField === undefined) {
        return null
    }

    const handleChangeOrder = (index: number) => {
        const desc = index === 1

        if (applied.desc === desc) {
            return
        }

        onChange({ ...applied, desc })
    }
    const selectedOrderIndex = applied.desc ? 1 : 0
    const handleFieldChange = (index: number) => {
        const sortBy = fields[index].field

        if (applied.sortBy === sortBy) {
            return
        }

        onChange({ ...applied, sortBy })
    }
    const orderOptions = [
        selectedField.ascLabel || "Ascending",
        selectedField.descLabel || "Descending"
    ]
    const orderBy = () => {
        return applied.desc
            ? selectedField.descLabel
                ? ` (${selectedField.descLabel})`
                : ""
            : selectedField.ascLabel
            ? ` (${selectedField.ascLabel})`
            : ""
    }

    const buttonText = `Order: ${selectedField.label}` + orderBy()
    const panelBody = (
        <div className="flex">
            <ExclusiveChoiceDropdown
                choices={fields.map(f => f.label)}
                selectedIndex={selectedFieldIndex}
                className="mr-1"
                testId="sort-options-sort-by"
                fitContentWidth={true}
                size="sm"
                onClick={handleFieldChange}
            />
            <ExclusiveChoiceDropdown
                choices={orderOptions}
                selectedIndex={selectedOrderIndex}
                className="ml-1"
                testId="sort-options-order-by"
                fitContentWidth={true}
                size="sm"
                onClick={handleChangeOrder}
            />
        </div>
    )

    return (
        <ButtonWithPanel
            ref={ref}
            isOpen={isOpen}
            classNameClosed={classNameClosed}
            classNameOpen={classNameOpen}
            buttonText={buttonText}
            panelBody={panelBody}
            testId={testId}
            onClickButton={toggleOpen}
            disabled={disabled}
        />
    )
}
